/**
 * RowActions — per-row action buttons for the BPA Findings grid.
 *
 * Provides:
 * - Add/view notes
 * - Assign/unassign finding
 * - Add/remove watchers
 * - Ignore/unignore finding
 * - Re-evaluate finding
 * - View finding details
 */
import IconButton from 'components/IconButton';
import React, { useMemo, useState } from 'react';

import BPAAssigneeModal from './BPAAssigneeModal';
import BPAFindingDetailModal from './BPAFindingDetailModal';
import BPAFindingIgnoreModal from './BPAFindingIgnoreModal';
import BPAFindingRecheckModal from './BPAFindingRecheckModal';
import BPAFindingSummaryCard from './BPAFindingSummaryCard';
import BPAWatcherModal from './BPAWatcherModal';
import { BPAFinding } from '../../types/bpaFindings.types';

interface RowActionsProps {
  rowData: BPAFinding;
  openNotesModal: (rowData: BPAFinding) => void;
}

const RowActions: React.FC<RowActionsProps> = ({ rowData, openNotesModal }) => {
  const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);
  const [isRecheckModalOpen, setIsRecheckModalOpen] = useState(false);
  const [isWatchersModalOpen, setIsWatchersModalOpen] = useState(false);
  const rowFindingIds = useMemo(() => [rowData.id], [rowData.id]);
  const haveUnreadNotes = rowData.total_notes > 0 && rowData.unread_notes > 0;
  const isIgnored = rowData.user_status === 'Ignored';
  const isFixed = rowData.scan_status === 'Fixed';

  const notesTooltip =
    rowData.total_notes > 0
      ? rowData.unread_notes > 0
        ? `View ${rowData.total_notes} notes (${rowData.unread_notes} unread)`
        : `View ${rowData.total_notes} notes`
      : 'Add notes';

  const handleViewDetailsClick = () => {
    setIsDetailsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailsModalOpen(false);
  };

  return (
    <div
      className="text-nowrap d-flex align-items-center justify-content-end"
      style={{ gap: '0.25rem' }}
    >
      {/* Notes */}
      <div style={{ position: 'relative' }} className="d-inline">
        {haveUnreadNotes && (
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              left: '-6px',
              width: '10px',
              height: '10px',
              backgroundColor: '#0E74BC',
              borderRadius: '50%',
              border: '2px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.2)',
              zIndex: 1000,
            }}
          />
        )}
        <IconButton
          id={`bpa_finding_notes_${rowData.id}`}
          iconClass="tim-icons icon-chat-33"
          size="sm"
          tooltip={notesTooltip}
          onClick={() => openNotesModal(rowData)}
          countBadge={rowData.total_notes}
        />
      </div>

      {/* Assign/Unassign */}
      <IconButton
        id={`bpa_finding_assignee_${rowData.id}`}
        iconClass="fa fa-user-plus"
        size="sm"
        tooltip="Click to assign/unassign"
        onClick={() => setIsAssigneeModalOpen(true)}
      />

      {/* Watchers */}
      <IconButton
        id={`bpa_finding_watchers_${rowData.id}`}
        iconClass="fa fa-users"
        size="sm"
        tooltip="Click to add/remove watchers"
        onClick={() => setIsWatchersModalOpen(true)}
        countBadge={rowData.watchers_count}
      />

      {/* Ignore/Unignore */}
      <IconButton
        id={`bpa_finding_ignore_${rowData.id}`}
        iconClass={isIgnored ? 'fa fa-circle-check' : 'fa fa-ban'}
        size="sm"
        tooltip={
          isFixed
            ? 'Fixed BPA findings cannot be ignored or unignored'
            : isIgnored
              ? 'Unignore this BPA finding'
              : 'Ignore this BPA finding'
        }
        onClick={() => setIsIgnoreModalOpen(true)}
        disabled={isFixed}
      />

      {/* Re-evaluate Finding */}
      <IconButton
        id={`bpa_finding_recheck_${rowData.id}`}
        iconClass="fa fa-sync-alt"
        size="sm"
        tooltip="Re-evaluate Finding"
        onClick={() => setIsRecheckModalOpen(true)}
      />

      {/* View Details */}
      <IconButton
        id={`bpa_finding_view_${rowData.id}`}
        iconClass="fa fa-eye"
        size="sm"
        tooltip="View finding details"
        onClick={handleViewDetailsClick}
      />

      {/* Detail Modal */}
      {isDetailsModalOpen && (
        <BPAFindingDetailModal
          findingId={rowData.id}
          onClose={handleCloseModal}
        />
      )}

      {/* Watchers Modal */}
      {isWatchersModalOpen && (
        <BPAWatcherModal
          onClose={() => setIsWatchersModalOpen(false)}
          findingIds={rowFindingIds}
          summaryCard={<BPAFindingSummaryCard rowData={rowData} />}
        />
      )}

      {/* Assignee Modal */}
      {isAssigneeModalOpen && (
        <BPAAssigneeModal
          finding={rowData}
          isOpen={isAssigneeModalOpen}
          toggle={() => setIsAssigneeModalOpen(false)}
        />
      )}

      {/* Ignore Modal */}
      {isIgnoreModalOpen && (
        <BPAFindingIgnoreModal
          finding={rowData}
          isOpen={isIgnoreModalOpen}
          toggle={() => setIsIgnoreModalOpen(false)}
          isIgnoreAction={!isIgnored}
        />
      )}

      {/* Recheck Modal */}
      {isRecheckModalOpen && (
        <BPAFindingRecheckModal
          finding={rowData}
          isOpen={isRecheckModalOpen}
          toggle={() => setIsRecheckModalOpen(false)}
        />
      )}
    </div>
  );
};

export default RowActions;
